import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import useApiService from '../../services/api'
import { Modal } from '../UI/Modal/Modal'
import { ContactResponse } from 'models/contacts'
import CreatableSelect from 'components/FormUtils/CreatableSelect'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { StyledEditorRHF } from 'components/Editor/EditorRHF'
import { PiPaperclip } from 'react-icons/pi'
import { toast } from 'react-toastify'
import to from 'await-to-js'
import {
  AttachmentList,
  Attachment,
  isNewAttachment,
} from 'components/Attachments/AttachmentList'
import {
  ALLOWED_EMAIL_ATTACHMENT_TYPES,
  MAX_EMAIL_ATTACHMENT_SIZE,
  EMAIL_ATTACHMENT_WARNING_THRESHOLD,
  getAcceptString,
} from '../../constants/fileTypes'
import { useFileAttachments } from '../../hooks/useFileAttachments'
import { handleEmailError } from '../NewCompanyPage/Modals/EmailsModal/handleEmailError'
import { SpecSheetPicker } from './SpecSheetPicker/SpecSheetPicker'
import {
  DS2026_MODAL_BODY_CLASS,
  DS2026_MODAL_BUTTON_CLASS,
  DS2026_MODAL_CONTENT_CLASS,
  DS2026_MODAL_FOOTER_CLASS,
  DsTextField,
  FieldGroup,
  FieldLabel,
  ds2026SelectStyles,
} from 'components/FbUI/Ds2026Field'
import { Ds2026ButtonProvider } from 'components/FbUI/Ds2026ButtonContext'

const formShape = z.object({
  to: z.string({ message: 'Please enter a valid email address' }).email(),
  subject: z.string().min(1),
  from_id: z.number({ message: 'Please select a from email' }),
  body: z.string().min(1),
})

type formValues = z.infer<typeof formShape>

// DS2026 modal chrome shared by the compose and empty-state renders. See
// DS2026-MODAL.md: rounded-2xl shell, rounded-lg close-icon hover, neutral-800
// title. Buttons get the DS2026 look from the wrapping <Ds2026ButtonProvider>.
const ds2026ChromeProps = {
  size: 'lg' as const,
  contentClassName: DS2026_MODAL_CONTENT_CLASS,
  closeIconClassName: 'rounded-lg hover:text-neutral-500',
  dialogFooterClassName: DS2026_MODAL_FOOTER_CLASS,
}

/**
 * DS2026 compose modal for emailing a single contact through the user's
 * connected inbox (Nylas). The recipient is fixed to the contact; the body
 * supports file attachments and generated product spec sheets (see
 * {@link SpecSheetPicker}). Renders an empty-state variant when the contact has
 * no email address.
 *
 * @param show - Whether the modal is open.
 * @param handleClose - Called to close the modal.
 * @param onSubmitSuccess - Optional callback fired after a successful send.
 * @param contact - The contact being emailed (source of the recipient address).
 */
export default function SendEmailModal(props: {
  show: boolean
  handleClose: () => void
  onSubmitSuccess?: () => void
  contact: ContactResponse
}) {
  const api = useApiService()
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [attachments, setAttachments] = useState<Attachment[]>([])

  const {
    fileInputRef,
    openFileSelect,
    handleFileSelect,
    validateAndAddFiles,
    createRemoveHandler,
  } = useFileAttachments({
    allowedTypes: ALLOWED_EMAIL_ATTACHMENT_TYPES,
    maxFileSize: MAX_EMAIL_ATTACHMENT_SIZE,
    maxTotalSize: MAX_EMAIL_ATTACHMENT_SIZE,
    onAdd: (newAttachments) =>
      setAttachments((prev) => [...prev, ...newAttachments]),
  })

  const { data: emailIntegrationOptions } =
    api.emailIntegration.useGetEmailIntegrationOptions()

  // Emailing a specific contact from their row → the recipient is fixed. Pick
  // the first available address (`||`, not `??`, so empty strings are skipped).
  // Cc/Bcc to other people would be a separate feature, not a recipient picker.
  const recipientEmail =
    props.contact.email_preferred ||
    props.contact.email_work ||
    props.contact.email_personal ||
    props.contact.email_generic ||
    ''
  const recipientName =
    `${props.contact.first_name} ${props.contact.last_name}`.trim()

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      to: recipientEmail,
      subject: '',
      body: '',
    } as formValues,
    resolver: zodResolver(formShape),
    // Errors first surface when a field is left (onTouched = validate on first
    // blur), then re-validate on every keystroke so the error clears the moment
    // the input becomes valid. Submit still runs a full validation.
    mode: 'onTouched',
    reValidateMode: 'onChange',
  })

  useMemo(() => {
    if (emailIntegrationOptions?.length === 1) {
      setValue('from_id', emailIntegrationOptions[0].value)
    }
  }, [emailIntegrationOptions, setValue])

  const handleRemoveAttachment = createRemoveHandler((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  })

  const onSubmit = handleSubmit(async (values) => {
    setIsSubmitting(true)

    try {
      // Build FormData payload
      const formData = new FormData()

      // Add email metadata
      formData.append(
        'to',
        JSON.stringify([
          {
            name: `${props.contact.first_name} ${props.contact.last_name}`,
            email: values.to,
          },
        ])
      )
      formData.append('subject', values.subject)
      formData.append('body', values.body)

      // Add file attachments directly (only new attachments, not existing ones)
      attachments.forEach((attachment) => {
        if (isNewAttachment(attachment)) {
          formData.append('attachments', attachment.file)
        }
      })

      const [err] = await to(
        api.emailIntegration.sendEmailMessage(values.from_id, formData)
      )

      setIsSubmitting(false)

      if (err) {
        await handleEmailError(err, api.emailIntegration)
        return
      }

      toast.success('Email sent successfully')
      props.onSubmitSuccess?.()
      props.handleClose()
    } catch (error) {
      setIsSubmitting(false)
      console.error('Error sending email:', error)
      toast.error('Failed to send email. Please try again.')
    }
  })

  if (!recipientEmail) {
    return (
      <Ds2026ButtonProvider>
        <Modal
          {...ds2026ChromeProps}
          open={props.show}
          onOpenChange={(open) => !open && props.handleClose()}
          title={<span className="text-neutral-800">Send Email</span>}
          cancelButtonText={'Close'}
          cancelButtonProps={{ className: DS2026_MODAL_BUTTON_CLASS }}
          onCancel={props.handleClose}
        >
          <p className="text-sm text-fg-secondary">
            This contact has no email address.
          </p>
        </Modal>
      </Ds2026ButtonProvider>
    )
  }

  return (
    <Ds2026ButtonProvider>
      <Modal
        {...ds2026ChromeProps}
        open={props.show}
        onOpenChange={(open) => !open && props.handleClose()}
        title={<span className="text-neutral-800">Send Email</span>}
        description={
          <span className="text-neutral-500">
            Compose an email to {props.contact.first_name}{' '}
            {props.contact.last_name} through your connected inbox.
          </span>
        }
        onAccept={onSubmit}
        acceptButtonText={isSubmitting ? 'Sending…' : 'Send'}
        acceptButtonProps={{ className: DS2026_MODAL_BUTTON_CLASS }}
        loading={isSubmitting}
        cancelButtonText={'Cancel'}
        cancelButtonProps={{ className: DS2026_MODAL_BUTTON_CLASS }}
        onCancel={props.handleClose}
      >
        <FieldGroup className={DS2026_MODAL_BODY_CLASS}>
          <div className="flex w-full flex-col gap-1.5">
            <FieldLabel>To</FieldLabel>
            {/* Fixed recipient (this contact): read-only, not a picker. */}
            <div className="flex min-h-[36px] items-center gap-2 rounded-lg border !border-neutral-300 bg-surface-sunken px-3.5 py-2">
              {recipientName && (
                <span className="shrink-0 text-sm font-medium tracking-[-0.2px] text-neutral-700">
                  {recipientName}
                </span>
              )}
              <span className="truncate text-sm tracking-[-0.2px] text-fg-secondary">
                {recipientEmail}
              </span>
            </div>
          </div>

          <div className="flex w-full flex-col gap-1.5">
            {/* htmlFor targets the select's search input (via inputId), the
                only labelable element react-select renders. */}
            <FieldLabel htmlFor="send-email-from">From</FieldLabel>
            <CreatableSelect
              control={control}
              name="from_id"
              inputId="send-email-from"
              placeholder="Select the inbox to send from"
              disableCreate
              options={emailIntegrationOptions}
              className="mt-0"
              styles={ds2026SelectStyles}
              ds2026
              onCreateOption={() => Promise.resolve(null)}
              isClearable={false}
            />
          </div>

          <DsTextField
            control={control}
            name="subject"
            label="Subject"
            placeholder="Add a subject"
          />

          <div className="flex w-full flex-col gap-1.5">
            {/* Visual label only: Quill's contenteditable body is not
                labelable via htmlFor, so the editor carries a matching
                aria-label (editorAriaLabel below) for assistive tech. */}
            <FieldLabel>Message</FieldLabel>
            {/* Borderless editor inside a DS2026 control shell so the whole
                field (text area + toolbar) reads as one input, with the brand
                focus ring on focus-within.

                The `[&_.ql-container]` overrides MUST live here on the wrapper,
                not on the editor's own className, because Quill resets the
                container element's className at mount, wiping anything scoped
                there.
                A global rule gives every `.ql-container` a 6px top radius +
                border; zeroing both makes the editor square so this wrapper's
                `overflow-hidden rounded-lg` clips it flush (no corner notch). */}
            <div className="overflow-hidden rounded-lg border !border-neutral-300 transition hover:!border-neutral-400 focus-within:!border-brand-active focus-within:ring-4 focus-within:ring-brand-active/20 [&_.ql-container]:!rounded-none [&_.ql-container]:!border-0">
              <StyledEditorRHF
                name={'body'}
                control={control}
                noMargin
                borderless
                className={
                  'min-h-28 [&_.ql-container]:min-h-28 [&_.ql-editor]:min-h-28'
                }
                // `.borderless-editor .ql-editor` is forced to padding:0, so the
                // text/placeholder would sit flush in the corner. editorPadding
                // sets inline !important padding to give it breathing room
                // (matches the DS2026 input px-3.5/py-2).
                editorPadding={'10px 14px'}
                editorAriaLabel="Message"
                placeholder={'Write your message…'}
                // Divider above the toolbar; strip the Quill toolbar's own
                // border + radius (a global rule adds them) so only this one
                // clean line shows inside the rounded shell.
                toolbarClassName="border-t border-neutral-200 [&_.ql-toolbar]:!border-0 [&_.ql-toolbar]:!rounded-none"
                // Actions live in the toolbar suffix (rendered OUTSIDE
                // `.ql-toolbar`), so they escape Quill's toolbar button CSS and
                // render as real controls, right-justified in the same row.
                toolbarSuffix={
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      aria-label="Attach a file"
                      disabled={isSubmitting}
                      onClick={isSubmitting ? undefined : openFileSelect}
                      className={`flex h-8 w-8 items-center justify-center rounded-md text-fg-secondary transition-colors ${
                        isSubmitting
                          ? 'cursor-not-allowed opacity-50'
                          : 'hover:bg-surface-sunken hover:text-brand-active'
                      }`}
                    >
                      <PiPaperclip size={18} />
                    </button>
                    <SpecSheetPicker
                      disabled={isSubmitting}
                      attachedFilenames={attachments.map((a) => a.filename)}
                      onAttach={(attachment) => {
                        // Same validation path as the paperclip flow: per-file
                        // and total size caps with the standard toasts. The
                        // returned boolean tells the picker whether to close.
                        const accepted = validateAndAddFiles(
                          [attachment.file],
                          attachments
                        )
                        if (accepted) {
                          setAttachments((prev) => [...prev, ...accepted])
                        }
                        return accepted !== null
                      }}
                    />
                  </div>
                }
              />
            </div>

            <AttachmentList
              attachments={attachments}
              onRemove={handleRemoveAttachment}
              disabled={isSubmitting}
              maxSize={MAX_EMAIL_ATTACHMENT_SIZE}
              warningThreshold={EMAIL_ATTACHMENT_WARNING_THRESHOLD}
            />

            <input
              type="file"
              hidden
              multiple
              aria-label="File upload"
              ref={fileInputRef}
              onChange={(e) => handleFileSelect(e, attachments)}
              accept={getAcceptString(ALLOWED_EMAIL_ATTACHMENT_TYPES)}
              disabled={isSubmitting}
            />
          </div>
        </FieldGroup>
      </Modal>
    </Ds2026ButtonProvider>
  )
}
